import type {
  ITenantInvoicing,
  ITenantInvoicingBlockingCheck,
  ITenantInvoicingBlockingCheckCode,
  ITenantInvoicingProvider,
  ITenantInvoicingSwitchProblem,
} from '@manager/api/types/tenant-invoicing-types';
import { i18n } from '@manager/plugins/i18n-plugin';
import { getApiError, translateApiErrorCode } from '@manager/utils/error-utils';
import { captureMessage } from '@sentry/vue';
import { isAxiosError } from 'axios';
import dayjs from 'dayjs';

type ITenantInvoicingState =
  | 'notOnKinnovisBilling'
  | 'notSetUp'
  | 'blocked'
  | 'ready'
  | 'switched';

export function deriveTenantInvoicingState(
  invoicing: ITenantInvoicing
): ITenantInvoicingState {
  // Locations still on Stripe move on their first booking or invoice.
  if (
    invoicing.provider === 'kinnovis' &&
    !isTenantInvoicingSwitchInterrupted(invoicing)
  ) {
    return 'switched';
  }
  if (!invoicing.usesKinnovisBilling) {
    return 'notOnKinnovisBilling';
  }
  // A tenant switched before set-up existed has no `setUpAt`.
  if (invoicing.setUpAt === null && invoicing.switchedAt === null) {
    return 'notSetUp';
  }
  if (invoicing.blockingChecks.length > 0) {
    return 'blocked';
  }
  return 'ready';
}

/** The switch never set the classic flag; activating again completes it. */
export function isTenantInvoicingSwitchInterrupted(
  invoicing: ITenantInvoicing
): boolean {
  return invoicing.provider === 'kinnovis' && !invoicing.usesKinnovisInvoicing;
}

/**
 * Since KIN-5214 the classic ranges number no Kinnovis invoice; the invoicing tab holds the sequences. For a
 * tenant on Kinnovis Billing they show only once billing-api says it still invoices through Stripe, so they
 * neither flash while the view loads nor show when billing-api cannot answer.
 */
export function showsClassicDocumentNumberRanges(
  usesKinnovisBilling: boolean | undefined,
  invoicing: ITenantInvoicing | undefined
): boolean {
  return usesKinnovisBilling === false || invoicing?.provider === 'stripe';
}

// Brand names, never translated.
export const tenantInvoicingProviderNames: Record<
  ITenantInvoicingProvider,
  string
> = {
  stripe: 'Stripe',
  kinnovis: 'Kinnovis',
};

const tenantInvoicingBlockingChecks: Record<
  ITenantInvoicingBlockingCheckCode,
  { messageKey: string; hintKey: string | null }
> = {
  billing_cutover: {
    messageKey: 'tenantInvoicing.check.billingCutover',
    hintKey: null,
  },
  invoice_templates: {
    messageKey: 'tenantInvoicing.check.invoiceTemplates',
    hintKey: 'tenantInvoicing.check.devTeam',
  },
  // Staff set up a sequence by hand in the number sequences card of this tab.
  number_sequences: {
    messageKey: 'tenantInvoicing.check.numberSequences',
    hintKey: 'tenantInvoicing.check.numberSequencesCard',
  },
  invoice_emails: {
    messageKey: 'tenantInvoicing.check.invoiceEmails',
    hintKey: 'tenantInvoicing.check.invoiceIssuedEmail',
  },
  legal_entities: {
    messageKey: 'tenantInvoicing.check.legalEntities',
    hintKey: 'tenantInvoicing.check.locationSettings',
  },
  location_details: {
    messageKey: 'tenantInvoicing.check.locationDetails',
    hintKey: 'tenantInvoicing.check.locationSettings',
  },
  // The tab shows the set-up button instead; only a refused activation lists it.
  set_up: {
    messageKey: 'tenantInvoicing.check.notSetUp',
    hintKey: null,
  },
};

function isKnownBlockingCheckCode(
  code: string
): code is ITenantInvoicingBlockingCheckCode {
  return Object.hasOwn(tenantInvoicingBlockingChecks, code);
}

const reportedUnknownCheckCodes = new Set<string>();

/**
 * A code the backend added before the frontend knew it is labelled with its raw code and
 * reported to Sentry once per session. It still blocks the switch.
 */
export function getTenantInvoicingBlockingCheckRow({
  code,
  detail,
}: ITenantInvoicingBlockingCheck) {
  const { t } = i18n.global;

  if (!isKnownBlockingCheckCode(code)) {
    if (!reportedUnknownCheckCodes.has(code)) {
      reportedUnknownCheckCodes.add(code);
      captureMessage(
        `Unknown tenant invoicing blocking check: ${code}`,
        'warning'
      );
    }
    return {
      code,
      detail,
      label: code,
      hint: t('tenantInvoicing.check.devTeam'),
    };
  }
  const { messageKey, hintKey } = tenantInvoicingBlockingChecks[code];
  return {
    code,
    detail,
    label: t(messageKey),
    hint: hintKey ? t(hintKey) : null,
  };
}

/** The checks the readiness list shows: the set-up button replaces `set_up`. */
export function getTenantInvoicingReadinessChecks(
  invoicing: ITenantInvoicing
): ITenantInvoicingBlockingCheck[] {
  return invoicing.blockingChecks.filter((check) => check.code !== 'set_up');
}

const switchBlockedCode = 'domain.tenant_invoicing.switch_blocked';

/** The documented 409 of a refused switch, or of a location refused on its first billing use. */
export function isTenantInvoicingSwitchBlockedError(error: unknown): boolean {
  return getApiError(error)?.error.code === switchBlockedCode;
}

export function getTenantInvoicingConflictChecks(
  error: unknown
): ITenantInvoicingBlockingCheck[] {
  if (!isAxiosError<ITenantInvoicingSwitchProblem>(error)) {
    return [];
  }
  return error.response?.data?.blockingChecks ?? [];
}

/**
 * A booking or invoice refused because its location could not move to Kinnovis Invoicing on
 * first use: the reason, then one line per failing check. `undefined` for any other error.
 */
export function getTenantInvoicingFirstUseBlockedMessage(
  error: unknown
): string | undefined {
  if (!isTenantInvoicingSwitchBlockedError(error)) {
    return undefined;
  }
  const checkLines = getTenantInvoicingConflictChecks(error)
    .map(getTenantInvoicingBlockingCheckRow)
    .map(({ label, detail }) => (detail ? `${label}: ${detail}` : label));

  return [translateApiErrorCode(switchBlockedCode), ...checkLines].join('\n');
}

export interface ITenantInvoicingWaitingDuration {
  unit: 'minutes' | 'hours' | 'days';
  count: number;
}

/** How long an invoice has waited, in the largest whole unit: minutes under an hour, hours under a day, then days. */
export function waitingDuration(
  waitingSince: string,
  now: Date | string = new Date()
): ITenantInvoicingWaitingDuration {
  const minutes = Math.max(0, dayjs(now).diff(dayjs(waitingSince), 'minute'));

  if (minutes < 60) {
    return { unit: 'minutes', count: minutes };
  }
  if (minutes < 24 * 60) {
    return { unit: 'hours', count: Math.floor(minutes / 60) };
  }
  return { unit: 'days', count: Math.floor(minutes / (24 * 60)) };
}
